import { mapListResponse } from './responseMapping/mapListResponse';

// Keys tried, in order, to find the shared identifier on either side of a
// merge when no explicit key is configured.
const DEFAULT_MERGE_KEYS = [
  'referenceId', 'ReferenceId',
  'candidateId', 'CandidateId',
  'entityId', 'EntityId',
  'id', 'Id', '_id',
];

export function createKeyResolver(keys = DEFAULT_MERGE_KEYS) {
  return function resolveKey(record) {
    if (!record || typeof record !== 'object') return '';
    for (const key of keys) {
      const value = record[key];
      if (value !== undefined && value !== null && value !== '') return String(value);
    }
    return '';
  };
}

// Every merge-key value a record carries — not just the first. The two sides
// of a merge often name their shared id differently, so indexing a join side
// under a single "best" key can miss matches the data actually supports.
function collectKeys(record, keys = DEFAULT_MERGE_KEYS) {
  if (!record || typeof record !== 'object') return [];
  const values = [];
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') values.push(String(value));
  }
  return values;
}

export const resolveMergeKey = createKeyResolver();

// Accepts a field name ('referenceId'), a priority list of field names, or a
// custom (record) => key function.
function normalizeKeyResolver(key) {
  if (typeof key === 'function') return key;
  if (typeof key === 'string') return createKeyResolver([key]);
  if (Array.isArray(key)) return createKeyResolver(key);
  return resolveMergeKey;
}

// Primary fields win over joined fields on name collisions; the row id stays
// stable for existing row navigation and detail routes.
export function defaultMergeRow(primaryRow, secondaryRow, key) {
  if (!secondaryRow || typeof secondaryRow !== 'object') return primaryRow;
  return {
    ...secondaryRow,
    ...primaryRow,
    id: primaryRow?.id ?? secondaryRow?.id ?? key,
  };
}

// The same envelope /module-data-list responds with, so mapListResponse
// resolves rows via payload.data and total via response.total unchanged.
export function toModuleListResponse(rows, total, meta = {}) {
  return { status: 'success', total, data: { data: rows, ...meta } };
}

/**
 * Generic merge helper for two arbitrary APIs.
 */
export async function mergeApiResponses({
  fetchPrimary,
  fetchSecondary,
  primaryArgs = [],
  secondaryArgs = [],
  extractPrimaryItems = (response) => response,
  extractSecondaryItems = (response) => response,
  primaryKey,
  secondaryKey,
  mergeItem = defaultMergeRow,
  combineResponse = ({ mergedItems }) => mergedItems,
}) {
  const [primaryResponse, secondaryResponse] = await Promise.all([
    fetchPrimary(...(typeof primaryArgs === 'function' ? primaryArgs() : primaryArgs)),
    fetchSecondary(...(typeof secondaryArgs === 'function' ? secondaryArgs() : secondaryArgs)),
  ]);

  const primaryItems = extractPrimaryItems(primaryResponse) ?? [];
  const secondaryItems = extractSecondaryItems(secondaryResponse) ?? [];
  const resolvePrimary = normalizeKeyResolver(primaryKey);
  const resolveSecondary = normalizeKeyResolver(secondaryKey);

  const secondaryByKey = new Map();
  secondaryItems.forEach((item) => {
    const key = resolveSecondary(item);
    if (key && !secondaryByKey.has(key)) secondaryByKey.set(key, item);
  });

  const mergedItems = primaryItems.map((item) => {
    const key = resolvePrimary(item);
    return mergeItem(item, secondaryByKey.get(key), key);
  });

  return combineResponse({
    primaryResponse,
    secondaryResponse,
    primaryItems,
    secondaryItems,
    mergedItems,
  });
}

const META_KEYS = ['fields', 'fieldConfig', 'tabs', 'tabField', 'actionRules', 'actions', 'columnActions'];

export function extractListMeta(response) {
  const payload = response?.data ?? response;
  const meta = {};
  for (const key of META_KEYS) {
    const value = response?.[key] ?? payload?.[key];
    const isEmpty = value == null || (Array.isArray(value) && value.length === 0);
    if (!isEmpty) meta[key] = value;
  }
  return meta;
}

const normalizeFieldName = (name) => String(name ?? '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

function insertFieldsAfter(fields, extraFields, anchor) {
  const anchors = (Array.isArray(anchor) ? anchor : [anchor])
    .map(normalizeFieldName)
    .filter(Boolean);
  const index = anchors.length
    ? fields.findIndex((field) =>
        anchors.includes(normalizeFieldName(field?.value))
        || anchors.includes(normalizeFieldName(field?.label)))
    : -1;
  if (index === -1) return [...fields, ...extraFields];

  const anchorOrder = Number(fields[index]?.order);
  const positioned = Number.isFinite(anchorOrder)
    ? extraFields.map((field, i) =>
        field.order == null || field.order === ''
          ? { ...field, order: anchorOrder + (i + 1) / (extraFields.length + 1) }
          : field)
    : extraFields;
  return [...fields.slice(0, index + 1), ...positioned, ...fields.slice(index + 1)];
}

const mapRows = (response, moduleName) => mapListResponse(response, moduleName).rows;

const mapPage = (response, moduleName) => {
  const { rows, total } = mapListResponse(response, moduleName);
  return { rows, total };
};

export function createModuleListFetchAdapter(fetcher, fixedModuleName) {
  return function fetchModuleList(moduleName, limit, offset, options = {}) {
    return fetcher(fixedModuleName ?? moduleName, limit, offset, options);
  };
}

function normalizeJoinSource(source) {
  if (!source || typeof source !== 'object') return null;
  const fetcher = source.fetchByKeys ?? source.fetch ?? source.function;
  if (typeof fetcher !== 'function') return null;
  return {
    ...source,
    fetchByKeys: typeof source.fetchByKeys === 'function' ? source.fetchByKeys : null,
    fetch: typeof source.fetch === 'function' ? source.fetch : null,
    function: typeof source.function === 'function' ? source.function : null,
    extractRows: typeof source.extractRows === 'function' ? source.extractRows : mapRows,
    mergeRow: typeof source.mergeRow === 'function' ? source.mergeRow : defaultMergeRow,
    fetchById: typeof source.fetchById === 'function' ? source.fetchById : null,
  };
}

function createLegacyJoinSources({
  fetchSecondaryByKeys,
  fetchSecondary,
  extractSecondaryRows,
  fetchSecondaryById,
  secondaryKey,
  mergeRow,
  secondaryFetchLimit,
}) {
  if (!fetchSecondaryByKeys && !fetchSecondary && !fetchSecondaryById) return [];
  return [normalizeJoinSource({
    key: secondaryKey,
    fetchByKeys: fetchSecondaryByKeys
      ? (moduleName, keys, options) => fetchSecondaryByKeys(moduleName, keys, options)
      : null,
    function: fetchSecondary
      ? ({ moduleName, options }) => fetchSecondary(moduleName, secondaryFetchLimit, 0, options)
      : null,
    extractRows: extractSecondaryRows,
    fetchById: fetchSecondaryById,
    mergeRow,
  })].filter(Boolean);
}

function createSourceKeyCollector(key) {
  if (typeof key === 'function') {
    return (record) => {
      const value = key(record);
      return value === undefined || value === null || value === '' ? [] : [String(value)];
    };
  }

  const keys = typeof key === 'string'
    ? [key]
    : (Array.isArray(key) && key.length ? key : DEFAULT_MERGE_KEYS);

  return (record) => collectKeys(record, keys);
}

function indexJoinRows(rows, collectSourceKeys) {
  const byKey = new Map();
  rows.forEach((row) => {
    for (const key of collectSourceKeys(row)) {
      if (!byKey.has(key)) byKey.set(key, row);
    }
  });
  return byKey;
}

function buildJoinContext({ moduleName, limit, offset, options, keys, primaryResponse, primaryRows, pageRows, total, source }) {
  return {
    moduleName,
    limit,
    offset,
    options,
    keys,
    primaryResponse,
    primaryRows,
    pageRows,
    total,
    source,
  };
}

async function invokeJoinSource(source, context) {
  if (typeof source.args === 'function') {
    const args = source.args(context);
    return (source.fetch ?? source.function ?? source.fetchByKeys)(...(Array.isArray(args) ? args : [args]));
  }
  if (Array.isArray(source.args)) {
    return (source.fetch ?? source.function ?? source.fetchByKeys)(...source.args);
  }
  if (source.fetchByKeys && Array.isArray(context.keys)) {
    return source.fetchByKeys(context.moduleName, context.keys, context.options, context);
  }
  const fetcher = source.fetch ?? source.function;
  if (typeof fetcher !== 'function') return null;
  return fetcher(context);
}

/**
 * @param {object} config
 *
 * Server-paginated mode (preferred):
 * @param {Function} [config.fetchPrimaryPage] async (moduleName, limit, offset, options) => response.
 * @param {Function} [config.extractPrimaryPage] (response, moduleName) => { rows, total }.
 * @param {Function} [config.fetchSecondaryByKeys] legacy single-join keyed fetch.
 *
 * Legacy full-set mode:
 * @param {Function} [config.fetchPrimary] async (moduleName, limit, offset, options) => response.
 * @param {Function} [config.extractPrimaryRows] (response, moduleName) => rows.
 * @param {Function} [config.fetchSecondary] legacy single-join full fetch.
 *
 * Shared:
 * @param {object[]} [config.joinSources] One or more join sources. A minimal
 *   source is `{ function: apiCall, key: 'id' }`. `function`/`fetch` receives
 *   a single context object. `fetchByKeys`, when provided, receives
 *   `(moduleName, keys, options, context)`.
 */
export function createMergedListFetcher({
  fetchPrimaryPage,
  extractPrimaryPage = mapPage,
  fetchSecondaryByKeys,
  joinSources = [],
  fetchPrimary,
  extractPrimaryRows = mapRows,
  fetchSecondary,
  extractSecondaryRows = mapRows,
  fetchSecondaryById,
  primaryKey,
  secondaryKey,
  mergeRow = defaultMergeRow,
  secondaryFetchLimit = 200,
  extraFields = [],
  extraFieldsAfter,
}) {
  const normalizedJoinSources = (
    Array.isArray(joinSources) && joinSources.length
      ? joinSources.map(normalizeJoinSource).filter(Boolean)
      : createLegacyJoinSources({
          fetchSecondaryByKeys,
          fetchSecondary,
          extractSecondaryRows,
          fetchSecondaryById,
          secondaryKey,
          mergeRow,
          secondaryFetchLimit,
        })
  );

  let callSeq = 0;

  async function lookupJoinRowById(source, collectSourceKeys, moduleName, key) {
    if (!source.fetchById || !key) return null;
    try {
      const record = await source.fetchById(moduleName, key);
      return record && collectSourceKeys(record).includes(String(key)) ? record : null;
    } catch (error) {
      console.error(`[mergedListApi] per-id join lookup failed for "${key}"`, error);
      return null;
    }
  }

  function warnOnNoMatch(pageRows, joinRows, joinByKey, resolvePageKey, collectSourceKeys) {
    if (import.meta.env?.DEV && joinRows.length && pageRows.length
        && !pageRows.some((row) => joinByKey.has(resolvePageKey(row)))) {
      console.warn('[mergedListApi] no merge-key match between primary and join rows', {
        samplePrimaryKey: resolvePageKey(pageRows[0]),
        sampleJoinKeys: collectSourceKeys(joinRows[0]),
        samplePrimaryRow: pageRows[0],
        sampleJoinRow: joinRows[0],
      });
    }
  }

  async function applyJoinSourceToRows(rows, source, response, moduleName, isStale) {
    if (!response) return rows;

    const joinRows = source.extractRows(response, moduleName) ?? [];
    const resolvePageKey = normalizeKeyResolver(source.primaryKey ?? primaryKey);
    const collectSourceKeys = createSourceKeyCollector(source.key);
    const joinByKey = indexJoinRows(joinRows, collectSourceKeys);

    warnOnNoMatch(rows, joinRows, joinByKey, resolvePageKey, collectSourceKeys);

    return Promise.all(
      rows.map(async (row) => {
        const key = resolvePageKey(row);
        const match = joinByKey.get(key)
          ?? (isStale() ? null : await lookupJoinRowById(source, collectSourceKeys, moduleName, key));
        return source.mergeRow(row, match, key);
      }),
    );
  }

  async function mergeRows(pageRows, joinResults, moduleName, isStale) {
    let mergedRows = pageRows;
    for (const result of joinResults) {
      mergedRows = await applyJoinSourceToRows(mergedRows, result.source, result.response, moduleName, isStale);
    }
    return mergedRows;
  }

  function buildResponse(mergedRows, total, responses) {
    const meta = {};
    for (const response of responses) {
      if (!response) continue;
      const extracted = extractListMeta(response);
      for (const key of Object.keys(extracted)) {
        if (meta[key] == null) meta[key] = extracted[key];
      }
    }
    if (extraFields.length) {
      meta.fields = insertFieldsAfter(meta.fields ?? [], extraFields, extraFieldsAfter);
    }
    return toModuleListResponse(mergedRows, total, meta);
  }

  async function fetchJoinResults({ moduleName, limit, offset, options, primaryResponse, primaryRows, pageRows, total }) {
    return Promise.all(
      normalizedJoinSources.map(async (source) => {
        try {
          const resolvePageKey = normalizeKeyResolver(source.primaryKey ?? primaryKey);
          const keys = [...new Set(pageRows.map(resolvePageKey).filter(Boolean))];
          if (source.fetchByKeys && keys.length === 0) return { source, response: null };
          const context = buildJoinContext({
            moduleName,
            limit,
            offset,
            options,
            keys,
            primaryResponse,
            primaryRows,
            pageRows,
            total,
            source,
          });
          const response = await invokeJoinSource(source, context);
          return { source, response };
        } catch (error) {
          console.error('[mergedListApi] join fetch failed — rendering page without that join', error);
          return { source, response: null };
        }
      }),
    );
  }

  async function fetchPaginated(moduleName, limit, offset, options, isStale) {
    const primaryResponse = await fetchPrimaryPage(moduleName, limit, offset, options);
    const page = extractPrimaryPage(primaryResponse, moduleName) ?? {};
    const pageRows = Array.isArray(page.rows) ? page.rows : [];
    const total = Number.isFinite(Number(page.total)) ? Number(page.total) : pageRows.length;

    const joinResults = await fetchJoinResults({
      moduleName,
      limit,
      offset,
      options,
      primaryResponse,
      primaryRows: pageRows,
      pageRows,
      total,
    });

    const mergedRows = await mergeRows(pageRows, joinResults, moduleName, isStale);
    return buildResponse(mergedRows, total, [primaryResponse, ...joinResults.map((result) => result.response)]);
  }

  async function fetchFullSet(moduleName, limit, offset, options, isStale) {
    const primaryResponse = await fetchPrimary(moduleName, limit, offset, options);
    const primaryRows = extractPrimaryRows(primaryResponse, moduleName) ?? [];
    const total = primaryRows.length;
    const pageRows = primaryRows.slice(offset, offset + limit);

    const joinResults = await fetchJoinResults({
      moduleName,
      limit,
      offset,
      options,
      primaryResponse,
      primaryRows,
      pageRows,
      total,
    });

    const mergedRows = await mergeRows(pageRows, joinResults, moduleName, isStale);
    return buildResponse(mergedRows, total, [primaryResponse, ...joinResults.map((result) => result.response)]);
  }

  return async function fetchMergedList(moduleName, limit = 10, offset = 0, options = {}) {
    const seq = ++callSeq;
    const isStale = () => seq !== callSeq;

    return fetchPrimaryPage
      ? fetchPaginated(moduleName, limit, offset, options, isStale)
      : fetchFullSet(moduleName, limit, offset, options, isStale);
  };
}
